import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaHardHat, FaUser, FaSignInAlt } from 'react-icons/fa';
import api from '../services/api';
import { connectSocket, disconnectSocket } from '../socket_';
import AidLoginOverlay from '../components/AidMubarak/AidLoginOverlay';
import { consumeRedirect } from '../utils/redirectAfterLogin';

const DEVICE_STORAGE_KEY = 'device_id';
const DEVICE_NAME_STORAGE_KEY = 'device_name';

const getOrCreateDeviceId = () => {
  try {
    let existingDeviceId = localStorage.getItem(DEVICE_STORAGE_KEY);

    if (existingDeviceId && existingDeviceId.trim()) {
      return existingDeviceId;
    }

    const newDeviceId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

    localStorage.setItem(DEVICE_STORAGE_KEY, newDeviceId);
    return newDeviceId;
  } catch (error) {
    console.error('❌ Erreur génération device_id :', error);
    return `dev_fallback_${Date.now()}`;
  }
};

const getOrCreateDeviceName = () => {
  try {
    let existingDeviceName = localStorage.getItem(DEVICE_NAME_STORAGE_KEY);

    if (existingDeviceName && existingDeviceName.trim()) {
      return existingDeviceName;
    }

    const platform = navigator.platform || 'UnknownPlatform';
    const language = navigator.language || 'unknown-lang';
    const generatedName = `${platform} - ${language}`;

    localStorage.setItem(DEVICE_NAME_STORAGE_KEY, generatedName);
    return generatedName;
  } catch (error) {
    console.error('❌ Erreur génération device_name :', error);
    return 'Navigateur inconnu';
  }
};

const buildDevicePayload = () => {
  const hasTouchSupport =
    'ontouchstart' in window ||
    (navigator.maxTouchPoints || 0) > 0 ||
    (navigator.msMaxTouchPoints || 0) > 0;

  return {
    device_id: getOrCreateDeviceId(),
    device_name: getOrCreateDeviceName(),
    user_agent: navigator.userAgent || null,
    platform: navigator.platform || null,
    language: navigator.language || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    screen_resolution:
      window.screen && window.screen.width && window.screen.height
        ? `${window.screen.width}x${window.screen.height}`
        : null,
    color_depth:
      window.screen && window.screen.colorDepth
        ? String(window.screen.colorDepth)
        : null,
    hardware_concurrency:
      typeof navigator.hardwareConcurrency === 'number'
        ? navigator.hardwareConcurrency
        : null,
    device_memory:
      typeof navigator.deviceMemory === 'number'
        ? String(navigator.deviceMemory)
        : null,
    touch_support: hasTouchSupport
  };
};

const LoginPage = ({ setUser, aidActive = false }) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const devicePayload = useMemo(() => buildDevicePayload(), []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        login,
        password,
        device: devicePayload
      };

      const { data } = await api.post('/auth/login', payload);

      localStorage.setItem('token', data.token);
      localStorage.setItem('user_name', data.user.name);
      localStorage.setItem('user_login', data.user.login);
      localStorage.setItem('user_role', data.user.role);
      localStorage.setItem('user_id', String(data.user.id));
      localStorage.setItem(
        'user_departements',
        JSON.stringify(data.user.departements || [])
      );
      localStorage.setItem(
        'user_privileges',
        JSON.stringify(data.user.privileges || [])
      );
      localStorage.setItem(
        'must_change_password',
        data.user.must_change_password ? '1' : '0'
      );

      setUser(data.user);

      disconnectSocket();
      connectSocket();

      navigate(consumeRedirect());
    } catch (err) {
      console.error('❌ Erreur login :', err);
      setError(err.response?.data?.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" style={aidActive ? { position: 'relative', overflow: 'hidden' } : {}}>
      {aidActive && <AidLoginOverlay />}
      <div className="login-card" style={aidActive ? { position: 'relative', zIndex: 1 } : {}}>
        <h3 className="text-center text-dark mb-4">
          <FaHardHat style={{ color: '#6d4e36' }} />{' '}
          <span style={{ color: '#6d4e36' }}>SHEMSPAIE</span>
        </h3>

        <form onSubmit={handleLogin}>
          <div className="form-group mb-3">
            <label className="text-dark fw-semibold">
              <FaUser className="me-2" /> Login
            </label>
            <input
              type="text"
              className="form-control"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="Entrez votre login"
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group mb-3">
            <label className="text-dark fw-semibold">
              <FaLock className="me-2" /> Mot de passe
            </label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez votre mot de passe"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="alert alert-danger text-center py-2">
              {error}
            </div>
          )}

          <div className="d-grid mt-3">
            <button className="btn btn-marron" type="submit" disabled={loading}>
              <FaSignInAlt className="me-2" />
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;